import React, { useEffect, useRef } from "react";
import { Input } from "antd";

const CODE_LENGTH = 8;

const VerificationCodeInput = ({
  value = "",
  onChange,
  disabled,
  autoFocus = true,
}) => {
  const inputRefs = useRef([]);
  const digits = String(value)
    .replace(/\D/g, "")
    .slice(0, CODE_LENGTH)
    .split("");

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) inputRefs.current[0].focus();
  }, [autoFocus]);

  const updateDigit = (index, rawValue) => {
    const entered = rawValue.replace(/\D/g, "");
    if (!entered) {
      const next = [...digits];
      next[index] = "";
      onChange?.(next.join(""));
      return;
    }

    if (entered.length > 1) {
      const next = Array(CODE_LENGTH).fill("");
      entered
        .slice(0, CODE_LENGTH)
        .split("")
        .forEach((digit, offset) => {
          if (index + offset < CODE_LENGTH) next[index + offset] = digit;
        });
      onChange?.(next.join(""));
      inputRefs.current[
        Math.min(index + entered.length, CODE_LENGTH - 1)
      ]?.focus();
      return;
    }

    const next = Array.from(
      { length: CODE_LENGTH },
      (_, position) => digits[position] || "",
    );
    next[index] = entered;
    onChange?.(next.join(""));
    inputRefs.current[Math.min(index + 1, CODE_LENGTH - 1)]?.focus();
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      inputRefs.current[Math.max(index - 1, 0)]?.focus();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      inputRefs.current[Math.min(index + 1, CODE_LENGTH - 1)]?.focus();
    } else if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      const next = Array.from(
        { length: CODE_LENGTH },
        (_, position) => digits[position] || "",
      );
      next[index - 1] = "";
      onChange?.(next.join(""));
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pastedCode = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pastedCode) return;
    event.preventDefault();
    onChange?.(pastedCode);
    inputRefs.current[Math.min(pastedCode.length, CODE_LENGTH) - 1]?.focus();
  };

  return (
    <div
      className="product-verification-inputs"
      onPaste={handlePaste}
      role="group"
      aria-label="8-digit verification code"
    >
      {Array.from({ length: CODE_LENGTH }, (_, index) => (
        <Input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          value={digits[index] || ""}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={`Verification digit ${index + 1}`}
          onFocus={(event) => event.target.select()}
          onChange={(event) => updateDigit(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;
